/**
 * Analytics Tab - Sales Performance Dashboard
 * Phase 7 - Connected to Real Backend APIs
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import MetricCard from '../components/analytics/MetricCard';
import SalesRankingChart from '../components/analytics/SalesRankingChart';
import ConversionFunnelChart from '../components/analytics/ConversionFunnelChart';
import ConversionTrendChart from '../components/analytics/ConversionTrendChart';
import AIInsightsPanel from '../components/analytics/AIInsightsPanel';
import TaskTrackingWidget from '../components/analytics/TaskTrackingWidget';
import PerformanceTable from '../components/analytics/PerformanceTable';
import { useAllAnalytics, usePerformanceTable } from '../hooks/useAnalytics';

const AnalyticsTab: React.FC = () => {
  const [rankingMetric, setRankingMetric] = useState<'revenue' | 'conversions' | 'conversionRate'>('revenue');

  // Fetch all analytics data from real backend APIs
  const {
    summary,
    salesRanking,
    funnel,
    trends,
    insights,
    tasks,
    isLoading,
    isError
  } = useAllAnalytics();

  // Fetch performance table data separately
  const { data: performanceTableData, isLoading: performanceLoading, isError: performanceError, error: performanceTableError } = usePerformanceTable();

  // Show loading state
  if (isLoading || performanceLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  // Show error state
  if (isError || performanceError) {
    const errorDetails = [
      summary.error ? `Summary: ${(summary.error as Error).message}` : null,
      salesRanking.error ? `Sales Ranking: ${(salesRanking.error as Error).message}` : null,
      funnel.error ? `Funnel: ${(funnel.error as Error).message}` : null,
      trends.error ? `Trends: ${(trends.error as Error).message}` : null,
      insights.error ? `Insights: ${(insights.error as Error).message}` : null,
      tasks.error ? `Tasks: ${(tasks.error as Error).message}` : null,
      performanceTableError ? `Performance Table: ${(performanceTableError as Error).message}` : null,
    ].filter(Boolean);

    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load analytics data. Please make sure you are logged in.
        </Alert>
        {errorDetails.length > 0 && (
          <Alert severity="info">
            <Typography variant="body2">Error details:</Typography>
            {errorDetails.map((err, i) => (
              <Typography key={i} variant="caption" display="block">{err}</Typography>
            ))}
          </Alert>
        )}
      </Container>
    );
  }

  // Extract data from API responses with fallbacks
  const summaryData = summary.data || {
    conversionRate: 0,
    conversionRateTrend: 0,
    monthlyRevenue: 0,
    revenueTrend: 0,
    activeSalesReps: 0,
    activeRepsChange: 0,
    avgDealCycle: 0,
    dealCycleTrend: 0
  };

  const salesRankingData = salesRanking.data || [];
  const funnelData = funnel.data || [];
  const trendData = trends.data || [];
  const aiInsights = insights.data || [];
  const tasksData = tasks.data || { summary: { total: 0, completed: 0, pending: 0, overdue: 0, completionRate: 0 }, upcomingTasks: [] };
  const taskSummary = tasksData.summary;
  const upcomingTasks = tasksData.upcomingTasks;
  const performanceData = performanceTableData || [];

  // Calculate active reps trend percentage
  const activeRepsTrend = summaryData.activeRepsChange / (summaryData.activeSalesReps - summaryData.activeRepsChange) * 100;

  // Format currency
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-HK', {
      style: 'currency',
      currency: 'HKD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Sales Analytics Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Comprehensive sales performance metrics and AI-powered insights
        </Typography>
      </Box>

      {/* Summary Metric Cards */}
      <Grid container spacing={3} mb={4}>
        {/* Total Conversion Rate */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Conversion Rate"
            value={`${summaryData.conversionRate}%`}
            icon={<TrendingUpIcon />}
            color="primary"
            trend={summaryData.conversionRateTrend}
            trendLabel="vs last month"
            loading={false}
          />
        </Grid>

        {/* Monthly Revenue */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Monthly Revenue"
            value={formatCurrency(summaryData.monthlyRevenue)}
            icon={<MoneyIcon />}
            color="success"
            trend={summaryData.revenueTrend}
            trendLabel="vs last month"
            loading={false}
          />
        </Grid>

        {/* Active Sales Reps */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Active Sales Reps"
            value={summaryData.activeSalesReps}
            icon={<PeopleIcon />}
            color="info"
            trend={activeRepsTrend}
            trendLabel="vs last month"
            loading={false}
          />
        </Grid>

        {/* Average Deal Cycle */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Average Deal Cycle"
            value={`${summaryData.avgDealCycle} days`}
            icon={<ScheduleIcon />}
            color="warning"
            trend={summaryData.dealCycleTrend}
            trendLabel="vs last month"
            loading={false}
            inverse // Negative trend is good for cycle time
          />
        </Grid>
      </Grid>

      {/* Charts Section - Phase 6.2 */}
      <Grid container spacing={3} mb={4}>
        {/* Sales Ranking Chart */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <SalesRankingChart
              data={salesRankingData}
              loading={false}
              metric={rankingMetric}
              onMetricChange={setRankingMetric}
            />
          </Paper>
        </Grid>

        {/* Conversion Funnel Chart */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <ConversionFunnelChart
              data={funnelData}
              loading={false}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Monthly Trend Chart - Full Width */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <ConversionTrendChart
          data={trendData}
          loading={false}
        />
      </Paper>

      {/* AI Insights Section - Phase 6.3 */}
      <Grid container spacing={3} mb={4}>
        {/* AI Insights Panel */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <AIInsightsPanel
              insights={aiInsights}
              loading={false}
            />
          </Paper>
        </Grid>

        {/* Task Tracking Widget */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <TaskTrackingWidget
              summary={taskSummary}
              upcomingTasks={upcomingTasks}
              loading={false}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Performance Table - Phase 6.4 */}
      <Paper sx={{ p: 3 }}>
        <PerformanceTable
          data={performanceData}
          loading={false}
        />
      </Paper>
    </Container>
  );
};

export default AnalyticsTab;
